import * as THREE from 'three';
import { DIRECTION_DELTA, type MazeDirection } from '../config/mazeStages';

type CameraPoseInput = {
  worldX: number;
  worldY: number;
  dir: MazeDirection;
};

export type MazeCameraRotation = { horizontal: number; vertical: number };
export type MazeCameraMode = 'free' | 'robot';

export const shouldRefitMazeCameraOnResize = (mode: MazeCameraMode) => mode === 'free';

export const MAZE_FREE_CAMERA_CONTROLS = {
  pan: false,
  rotate: false,
  zoom: false,
} as const;

export const getMazeFreeCameraRotation = (
  distance: number,
  rotation: MazeCameraRotation,
) => {
  const yaw = THREE.MathUtils.degToRad(THREE.MathUtils.clamp(rotation.horizontal, -100, 100) * 1.8);
  const pitch = THREE.MathUtils.degToRad(THREE.MathUtils.clamp(rotation.vertical, -100, 100) * 0.75);
  const quaternion = new THREE.Quaternion()
    .setFromAxisAngle(new THREE.Vector3(0, 0, 1), yaw)
    .multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), pitch));
  return {
    positionOffset: new THREE.Vector3(0, 0, distance).applyQuaternion(quaternion),
    up: new THREE.Vector3(0, 1, 0).applyQuaternion(quaternion),
  };
};

export const getRobotViewCameraPose = ({ worldX, worldY, dir }: CameraPoseInput) => {
  const delta = DIRECTION_DELTA[dir];
  const forward = new THREE.Vector3(delta.x, -delta.y, 0).normalize();
  const position = new THREE.Vector3(worldX, worldY, 2.25).addScaledVector(forward, -2.15);
  const target = new THREE.Vector3(worldX, worldY, 0.58).addScaledVector(forward, 0.35);
  return { position, target, forward };
};

export const offsetRobotViewCameraPose = (
  pose: ReturnType<typeof getRobotViewCameraPose>,
  baselineCenter: THREE.Vector3,
  currentCenter: THREE.Vector3,
) => {
  const centerMovement = currentCenter.clone().sub(baselineCenter);
  return {
    position: pose.position.clone().add(centerMovement),
    target: pose.target.clone().add(centerMovement),
    forward: pose.forward.clone(),
  };
};